/**
 * PLAT-11099 — the chapterization run.
 *
 * Compose → post → validate → **one** write. The hook owns the request and the
 * in-flight and failure state the overlay renders; where the chapters land is
 * the caller's, in the shape every other reel write takes.
 *
 * Nothing about the reel is touched on the way to a failure (DS10): the write
 * happens once, at the end, only if the response survived validation. There is
 * no partial application and no silent no-op.
 */
import { useAIPromptRunLog } from '@src/monitoring/hooks/useAIPromptRunLog';
import {
  generateReelChapterization,
  PROMPT_GENERATE_ENDPOINT,
  ReelChapterizationError,
} from '@src/repositories/ReelChapterizationRepository';
import { constrainChapterization } from '@src/services/reel/chapterizationConstraints';
import { chapterizationInstructions } from '@src/services/reel/chapterizationInstructions';
import {
  CHAPTERIZE_PROMPT_TITLE,
  chapterizationBaseText,
} from '@src/services/reel/reelChapterizationBaseText';
import {
  chapterizationBoundaries,
  restoreOmittedSections,
} from '@src/services/reel/chapterizationResponse';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useRef, useState } from 'react';

import type { ConstrainedChapterization } from '@src/services/reel/chapterizationConstraints';
import type { ChapterizationOptions } from '@src/services/reel/chapterizationInstructions';
import type { ChapterizationResult } from '@src/services/reel/chapterizationResponse';
import type { ReelSection } from '@src/services/reel/deriveReelSections';
import type {
  ReelChapter,
  ReelSectionName,
} from '@src/services/reel/reelTypes';
import type { Timeline } from '@src/editor/models/Timeline';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

const logger = LogManager.getLogger('ReelEditor:useReelChapterization');

/** Context BY REFERENCE — the backend resolves the Brand Kit from it. */
export type ReelChapterizationContext = {
  storyId?: string;
  showcaseId?: string;
  clipId?: string;
};

export type ReelChapterizationInput = {
  timeline: Timeline;
  /** The reel as it plays: live Sections only, in playback order. */
  sections: ReadonlyArray<Pick<ReelSection, 'id' | 'elementIds'>>;
  names?: Record<string, ReelSectionName | undefined>;
  /** The reel's chapters, for the run to fit around (PLAT-11154 S4). */
  chapters?: readonly ReelChapter[];
  /**
   * PLAT-11154 — the Sections the creator ticked. Absent is the whole reel; a
   * set is a subset run, which **preserves** rather than replaces (E8).
   */
  movableSectionIds?: ReadonlySet<string>;
  options: Omit<ChapterizationOptions, 'movableSectionNumbers'>;
};

/**
 * What a run resolves to. The order covers **every live Section**, not only the
 * ones that had words to send (`restoreOmittedSections`), so the caller can hand
 * it straight to `ReorderReelSectionsCommand`, which takes nothing less than a
 * whole permutation.
 */
export type ReelChapterizationOutcome = ChapterizationResult &
  Partial<Pick<ConstrainedChapterization, 'preservedTitleSources'>>;

export type ReelChapterizationRun = {
  /**
   * Runs the prompt over the reel and resolves to the order to play it in and
   * the boundaries to write, or to `null` when there was nothing to send, a run
   * is already in flight, or the run failed — in which case `error` says why.
   */
  chapterize: (
    input: ReelChapterizationInput,
  ) => Promise<ReelChapterizationOutcome | null>;
  isRunning: boolean;
  error?: string;
  /** Reports a failure that happened after the run, as the run's own (S8). */
  reportFailure: (message: string) => void;
  clearError: () => void;
};

export function useReelChapterization(
  context: ReelChapterizationContext,
  datoClientStore: DatoClientStore | undefined,
): ReelChapterizationRun {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // PLAT-11207 — every run leaves an AI Prompt Log record, success or failure.
  const logPromptRun = useAIPromptRunLog();

  // Mirrored so `chapterize` keeps one identity for the life of the panel.
  const contextRef = useRef(context);
  contextRef.current = context;

  // Resolved once per session and held: the record's id does not change. Its
  // type is kept alongside for the AI Prompt Log record (PLAT-11207).
  const promptIdRef = useRef<string | null>(null);
  const promptTypeRef = useRef<string | undefined>(undefined);
  // Read synchronously — `isRunning` state has not committed yet when a second
  // press lands in the same tick.
  const inFlightRef = useRef(false);

  const chapterize = useCallback(
    async (
      input: ReelChapterizationInput,
    ): Promise<ReelChapterizationOutcome | null> => {
      if (inFlightRef.current) return null;

      const { text, sectionIds } = chapterizationBaseText(
        input.timeline,
        input.sections,
        input.names,
        input.chapters,
      );
      if (!text.trim() || sectionIds.length === 0) {
        // Every Section is empty, off, or cut away. There is nothing to group.
        logger.warn('Declining to chapterize a reel with no words to read', {
          sectionCount: input.sections.length,
        });
        setError('This reel has no transcript to read.');
        return null;
      }

      inFlightRef.current = true;
      setIsRunning(true);
      setError(undefined);

      try {
        if (!datoClientStore) {
          throw new ReelChapterizationError(
            'The AI service is unavailable in this context',
          );
        }
        if (!promptIdRef.current) {
          const prompt =
            await datoClientStore.aiPromptRepository.getPromptByTitle(
              CHAPTERIZE_PROMPT_TITLE,
            );
          if (!prompt?.id) {
            throw new ReelChapterizationError(
              `AI Prompt "${CHAPTERIZE_PROMPT_TITLE}" not found in DatoCMS`,
            );
          }
          promptIdRef.current = prompt.id;
          promptTypeRef.current = prompt.promptType ?? undefined;
        }
        const promptId = promptIdRef.current;

        // Every live Section, including the ones with nothing to read that the
        // composed text left out — the order the caller needs back is over the
        // whole reel, not over the part the model saw.
        const allSectionIds = input.sections.map(section => section.id);
        const isFixed = (id: string) =>
          !!input.movableSectionIds && !input.movableSectionIds.has(id);

        // PLAT-11154 S8 — a subset of everything is the ordinary run, composed
        // as `all`, so there is no second code path for it. Asked of the whole
        // reel: a Section the creator left unticked constrains the result even
        // when it had no words to send.
        const isSubset = allSectionIds.some(isFixed);
        // The model, though, is only told about Sections it can actually see.
        const tellsModel = sectionIds.some(isFixed);

        const result = await logPromptRun(
          {
            promptTitle: CHAPTERIZE_PROMPT_TITLE,
            promptType: promptTypeRef.current,
            aiPromptId: promptId,
            endpoint: PROMPT_GENERATE_ENDPOINT,
            ...contextRef.current,
          },
          () =>
            generateReelChapterization({
              promptId,
              ...contextRef.current,
              baseText: text,
              instructions: chapterizationInstructions({
                ...input.options,
                movableSectionNumbers: tellsModel
                  ? sectionIds
                      .map((id, index) => (isFixed(id) ? 0 : index + 1))
                      .filter(Boolean)
                  : undefined,
              }),
            }),
        );

        // DS8 — validated against the Sections that were actually sent, which is
        // what the ordinals in the response index into.
        const unconstrained = chapterizationBoundaries(result, sectionIds, {
          reorder: input.options.reorder,
        });
        // …then widened back to the whole reel, so a Section with nothing left
        // to read holds its place instead of dropping out of the order.
        const restored = restoreOmittedSections(unconstrained, allSectionIds);
        // PLAT-11154 — and then held to what the creator protected. The model is
        // told the rule; this is where it is guaranteed.
        const validated = isSubset
          ? constrainChapterization(restored, {
              currentSectionIds: allSectionIds,
              currentChapters: input.chapters ?? [],
              movableSectionIds: input.movableSectionIds ?? new Set(),
            })
          : restored;

        if (validated.boundaries.length === 0) {
          throw new ReelChapterizationError(
            'The model did not return a usable set of chapters',
          );
        }

        logger.info('Chapterized a reel', {
          clipId: contextRef.current.clipId,
          sectionCount: sectionIds.length,
          chapterCount: validated.boundaries.length,
          reordered: !!input.options.reorder,
        });
        return validated;
      } catch (caught) {
        logger.error('Failed to chapterize a reel', extractErrorInfo(caught));
        setError(
          caught instanceof Error
            ? caught.message
            : 'Could not chapter this reel',
        );
        return null;
      } finally {
        inFlightRef.current = false;
        setIsRunning(false);
      }
    },
    [datoClientStore, logPromptRun],
  );

  const clearError = useCallback(() => setError(undefined), []);

  // PLAT-11153 S8 — the write half of a run happens in the caller, so a failure
  // there has to come back here to be seen: the overlay renders one error, and a
  // reorder the command refused is as much a failed run as a request that never
  // landed. Without this the dialog just sits there having done nothing.
  const reportFailure = useCallback((message: string) => {
    logger.error('A chapterization failed after the run', { message });
    setError(message);
  }, []);

  return { chapterize, isRunning, error, reportFailure, clearError };
}
